import React, { useRef, useState } from 'react'
import { ThumbsUp, ThumbsDown, MessageCircle } from 'lucide-react'
import { useAddUserFeedback } from '../../hooks/db/useAddUserFeedback'

export type FeedbackType = 'good' | 'bad' | 'custom'

type FeedbackBlockProps = {
  conversationId: string
  messageId: string
  sender: string
  content: string,
  question: string
}

const Feedback: React.FC<FeedbackBlockProps> = ({
  conversationId,
  messageId,
  sender,
  content,
  question
}) => {

  const [showTextarea, setShowTextarea] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [customFeedback, setCustomFeedback] = useState('')
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const { addUserFeedback } = useAddUserFeedback()
  const [feedbackSelected, setFeedbackSelected] = useState<FeedbackType | null>(null)
  const endRef = useRef<HTMLButtonElement>(null)
  const [customFeedbackError, setCustomFeedbackError] = useState('')
  const [showLoading, setShowLoading] = useState(false)
  

  const sendFeedback = async (type: FeedbackType, comment?: string) => {
    try {
      setShowLoading(true)
      await addUserFeedback(
        conversationId,
        messageId,
        sender,
        content,
        question,
        type,
        comment || '',
      )
      setShowLoading(false)
      setShowSuccess(true)
      setFeedbackSubmitted(true)
      setShowTextarea(false)
      setCustomFeedback('')
      setFeedbackSelected(type)
      setTimeout(() =>  setShowSuccess(false), 1000)
    } catch (error: any) {
      if (error.response?.status === 400) {
        alert('You have already submitted feedback for this message.')
      } else {
        console.error('Feedback error:', error)
      }
      setShowLoading(false)
    }
  }

  const showFeddbackArea = () => {
    setShowTextarea(!showTextarea)
    setTimeout(()=>{
      endRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 300)
  }

  const sendCustomFeedBack = () => {
    if (customFeedback.trim() === '') {
      setCustomFeedbackError('Please enter the feedback')
      return
    } else {
      setCustomFeedbackError('')
      sendFeedback('custom', customFeedback)
    }
  }

  return (
    <div className="text-sm flex w-full mb-4 justify-center flex-col">
     <div className="flex gap-4 items-center text-gray-500 justify-center ">
        <div>
          <button
                onClick={() => sendFeedback('good')}
                title="Thumbs Up"
                disabled={feedbackSubmitted}
                className={`
                  transition duration-200
                  ${feedbackSubmitted ? 'cursor-not-allowed' : 'hover:text-green-600'}
                  ${feedbackSelected === 'good' ? 'text-green-600 font-bold' : 'text-gray-500'}
                `}
              >
                <ThumbsUp size={18} strokeWidth={2} />
          </button>

          <button
              onClick={() => sendFeedback('bad')}
              title="Thumbs Down"
              disabled={feedbackSubmitted}
              className={`
                transition duration-200 ml-1
                ${feedbackSubmitted ? 'cursor-not-allowed' : 'hover:text-blue-600'}
                ${feedbackSelected === 'bad' ? 'text-red-600 font-bold' : 'text-gray-500'}
              `}
            >
              <ThumbsDown size={18} strokeWidth={2} />
          </button>

          <button
              onClick={() => showFeddbackArea() }
              title="Leave Feedback"
              disabled={feedbackSubmitted}
              className={`
                transition duration-200 ml-1
                ${feedbackSubmitted ? 'cursor-not-allowed' : 'hover:text-blue-600'}
                ${feedbackSelected === 'custom' ? 'text-blue-600 font-bold' : 'text-gray-500'}
              `}
            >
              <MessageCircle size={18} strokeWidth={2} />
          </button>
        </div>
      </div>

      {showTextarea && !feedbackSubmitted && (
        <div className="mt-2">
          <textarea
            value={customFeedback}
            onChange={(e) => setCustomFeedback(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="Leave detailed feedback..."
          />
          {customFeedbackError && (
              <>
                <p>
                  <span className="text-red-500 text-sm w-full">{customFeedbackError}</span>
                </p>
              </>
          )}
          <button
            onClick={() => sendCustomFeedBack()}
            ref={endRef}
            className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded text-sm transition"
          >
            Submit
          </button>
        </div>
      )}
      {showLoading && (
        <div className="text-gray-600 text-xs mt-1 text-center">
          <span className="animate-pulse">Submitting feedback...</span>
        </div>
      )}
      {showSuccess && (
        <div className="text-green-600 text-xs mt-1 text-center">Thank you for your feedback!</div>
      )}
    </div>
  )
}

export default Feedback
